/**
 * locale — Datum/Zahlen-Formatierung folgt der aktiven i18n-Sprache.
 *
 * Vorher waren Intl-/date-fns-Aufrufe hart auf 'de-DE' bzw. date-fns `de`
 * verdrahtet → im EN-Modus erschienen weiterhin deutsche Datums-/Zahlformate.
 * Diese Helfer lesen die aktive Sprache aus i18n zur Aufrufzeit.
 */
import { de, enUS, type Locale } from 'date-fns/locale'
import { formatDistanceToNow } from 'date-fns'
import i18n from '../i18n'

function isEnglish(): boolean {
  const lng = i18n.resolvedLanguage ?? i18n.language ?? 'de'
  return lng.toLowerCase().startsWith('en')
}

/** BCP-47-Locale für Intl / toLocale*-APIs anhand der aktiven Sprache. */
export function activeBcp47(): string {
  return isEnglish() ? 'en-US' : 'de-DE'
}

/** date-fns-Locale-Objekt anhand der aktiven Sprache. */
export function activeDateFnsLocale(): Locale {
  return isEnglish() ? enUS : de
}

/** True, wenn die aktive Sprache Englisch ist (für sprachabhängige Format-Strings). */
export function isEnLocale(): boolean {
  return isEnglish()
}

/**
 * Issue #49 (Nachbesserung): Das BE serialisiert Fenstergrenzen mit
 * `Time.zone.now...end_of_day.iso8601` INNERHALB von `Time.use_zone(current_user.time_zone)`
 * (siehe ApiController#set_time_zone) — der ISO-String traegt also bereits den
 * korrekten Offset der Zeitzone, in der das BE das Fenster gebildet hat
 * (Server-Default UTC, sonst die User-Zeitzone). Der vorherige Fix nutzte
 * `toLocaleDateString` ohne `timeZone`-Option und rechnete den Zeitstempel damit
 * in die Zeitzone des BETRACHTERS um — bei einem Betrachter westlich der
 * Fenster-Zeitzone kann das erneut ueber eine Mitternachtsgrenze zurueckspringen.
 *
 * Der Kalendertag, den die Fenstergrenze meint, steht bereits woertlich im
 * Datums-Teil des ISO-Strings (unabhaengig vom Offset-Suffix — der Offset
 * beschreibt nur den Abstand zu UTC, nicht welcher Kalendertag gemeint ist).
 * Wir extrahieren ihn daher direkt per Regex statt ueber `new Date(iso)` zu
 * gehen (das wuerde in Betrachterzeit konvertieren) und formatieren ihn mit
 * `timeZone: 'UTC'`, damit `toLocaleDateString` die extrahierten Komponenten
 * unveraendert durchreicht.
 */
export function formatLocalDate(iso: string | null | undefined): string {
  return formatLocalDateWithPattern(iso, displayPreferences.date_format)
}

/**
 * Wie formatLocalDate, aber mit explizit übergebenem date_format-Pattern statt
 * dem Modul-State — für Aufrufer, die (wie AccountInfoSection) den Account
 * bereits als Prop haben und unabhängig davon testbar bleiben sollen, ob
 * setDisplayPreferences zuvor aufgerufen wurde.
 */
export function formatLocalDateWithPattern(
  iso: string | null | undefined,
  pattern: string | null | undefined,
): string {
  if (!iso) return '—'
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!match) return '—'
  const [, year, month, day] = match
  const d = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)))
  if (Number.isNaN(d.getTime())) return '—'
  const spec = deriveDateFormatSpec(pattern)
  if (spec) {
    return d.toLocaleDateString(spec.locale, { ...spec.options, timeZone: 'UTC' })
  }
  return d.toLocaleDateString(activeBcp47(), { timeZone: 'UTC' })
}

/**
 * heliumtracker-web#24: BE liefert "vor X Minuten" teils als reine Zahl mit
 * sehr grossen Werten (z.B. 383961 Minuten bei lange inaktiven Hotspots).
 * Roh mit dem i18n-Key "minutesAgo" gerendert entstand daraus wörtlich
 * "vor 383961 Minuten" statt einer lesbaren relativen Zeit. Formatiert daher
 * ueber date-fns (gleiches Muster wie die ISO-basierten Zeitangaben).
 */
export function formatMinutesAgo(minutes: number | null | undefined): string | null {
  if (minutes == null || !Number.isFinite(minutes)) return null
  const date = new Date(Date.now() - minutes * 60_000)
  return formatDistanceToNow(date, { addSuffix: true, locale: activeDateFnsLocale() })
}

/**
 * Issue #124 (= #55): Konto-Einstellungen date_format/time_format/num_digits
 * (Settings -> Display, siehe routes/_auth.settings.tsx) wurden zwar per
 * PATCH /account/me persistiert, aber nirgends ausgewertet — alle Formatter
 * richteten sich ausschliesslich nach der aktiven i18n-Sprache.
 *
 * Zentraler Modul-State: `setDisplayPreferences` wird dort aufgerufen, wo der
 * Account geladen wird (siehe features/settings/useAccount.ts), und
 * `formatLocalDate`/`formatLocalDateTime`/`formatDecimal` leiten daraus ihr
 * Format ab. Ohne gesetzte Praeferenzen (z.B. vor dem ersten Account-Fetch,
 * im Login-Screen, in Tests) bleibt das bisherige sprachbasierte Verhalten
 * unveraendert erhalten (Requirement (c)).
 */
export type DisplayPreferences = {
  date_format?: string | null
  time_format?: string | null
  num_digits?: number | null
}

let displayPreferences: DisplayPreferences = {}

/** Setzt die aktuellen Anzeige-Einstellungen (Modul-State). `null`/`undefined` löscht sie. */
export function setDisplayPreferences(prefs: DisplayPreferences | null | undefined): void {
  displayPreferences = prefs ? { ...prefs } : {}
}

/** Nur für Tests: setzt den Modul-State auf "keine Einstellungen" zurück. */
export function resetDisplayPreferences(): void {
  displayPreferences = {}
}

type DateFormatSpec = { locale: string; options: Intl.DateTimeFormatOptions }

/**
 * Pattern -> Intl.DateTimeFormat-Spec. Die Werte kommen 1:1 aus den
 * DATE_FORMATS-Optionen in routes/_auth.settings.tsx.
 *
 * Statt die Trennzeichen/Reihenfolge manuell zusammenzubauen, nutzen wir
 * gezielt die Locale, deren native Intl-Formatierung mit day/month/year
 * '2-digit' exakt dem gewünschten Muster entspricht (de-DE -> Punkte in
 * DD.MM-Reihenfolge, en-GB -> Slashes in DD/MM-Reihenfolge, en-US ->
 * Slashes in MM/DD-Reihenfolge, sv-SE -> Bindestriche in YYYY-MM-DD-
 * Reihenfolge — ein etabliertes Intl-Idiom für ISO-artige Ausgabe).
 */
const DATE_FORMAT_SPECS: Record<string, DateFormatSpec> = {
  'DD.MM.YYYY': { locale: 'de-DE', options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
  'DD/MM/YYYY': { locale: 'en-GB', options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
  'MM/DD/YYYY': { locale: 'en-US', options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
  'YYYY-MM-DD': { locale: 'sv-SE', options: { day: '2-digit', month: '2-digit', year: 'numeric' } },
}

/** Pattern -> Intl.DateTimeFormat-Zeitoptionen. Werte aus TIME_FORMATS in routes/_auth.settings.tsx. */
const TIME_FORMAT_SPECS: Record<string, Intl.DateTimeFormatOptions> = {
  'HH:mm': { hour: '2-digit', minute: '2-digit', hour12: false },
  'H:mm': { hour: 'numeric', minute: '2-digit', hour12: false },
  'hh:mm a': { hour: '2-digit', minute: '2-digit', hour12: true },
  'h:mm a': { hour: 'numeric', minute: '2-digit', hour12: true },
}

/** Leitet aus einem gespeicherten date_format-Pattern die Intl-Spec ab, oder `null` bei unbekanntem/fehlendem Pattern. */
export function deriveDateFormatSpec(pattern: string | null | undefined): DateFormatSpec | null {
  if (!pattern) return null
  return DATE_FORMAT_SPECS[pattern] ?? null
}

/** Leitet aus einem gespeicherten time_format-Pattern die Intl-Zeitoptionen ab, oder `null` bei unbekanntem/fehlendem Pattern. */
export function deriveTimeFormatOptions(
  pattern: string | null | undefined,
): Intl.DateTimeFormatOptions | null {
  if (!pattern) return null
  return TIME_FORMAT_SPECS[pattern] ?? null
}

/**
 * formatLocalDateTime — Datum + Uhrzeit eines ISO-Zeitstempels, unter
 * Beachtung der Konto-Einstellungen date_format/time_format.
 *
 * Anders als formatLocalDate (Kalendertag-Extraktion, siehe Kommentar oben —
 * dort geht es um Fenstergrenzen, die absichtlich zeitzonen-unabhaengig
 * bleiben muessen) formatiert diese Funktion einen echten Zeitpunkt
 * (z.B. "zuletzt aktiv") und rechnet daher bewusst in die Zeitzone des
 * Betrachters um (kein `timeZone: 'UTC'`) — identisch zum bisherigen
 * Verhalten der umgestellten Aufrufer (`new Date(iso).toLocaleString(...)`).
 */
export function formatLocalDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'

  const dateSpec = deriveDateFormatSpec(displayPreferences.date_format)
  const timeOptions = deriveTimeFormatOptions(displayPreferences.time_format)

  if (!dateSpec && !timeOptions) {
    return d.toLocaleString(activeBcp47(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const locale = dateSpec?.locale ?? activeBcp47()
  const options: Intl.DateTimeFormatOptions = {
    ...(dateSpec?.options ?? { day: '2-digit', month: '2-digit', year: 'numeric' }),
    ...(timeOptions ?? { hour: '2-digit', minute: '2-digit' }),
  }
  return d.toLocaleString(locale, options)
}

/**
 * formatDecimal — Zahl mit der Konto-Einstellung num_digits (Settings ->
 * Display -> "Decimal places"), falls gesetzt, sonst `fallbackDigits`
 * (bisheriges Verhalten der jeweiligen Aufrufstelle).
 *
 * Normalisiert -0 zu 0 (gleiches Muster wie lib/fmt.ts).
 */
export function formatDecimal(value: number | null | undefined, fallbackDigits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—'
  const digits = displayPreferences.num_digits ?? fallbackDigits
  const safe = Object.is(value, -0) ? 0 : value
  return safe.toLocaleString(activeBcp47(), {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}
